import { useCapabilities, usePlansQuery } from '@/features/billing/queries';
import {
  CAPABILITY_COPY,
  type CapabilityIdentifier,
} from '@/features/billing/capability-identifiers';
import { planTierName, resolveUnlockingPlan } from '@/features/billing/plan-helpers';
import type { BillingPlan } from '@/lib/billing';
import type { Access } from '@/features/access/access';
import { useRoleAccess } from '@/features/access/use-role-access';

/**
 * Everything a locked state needs to explain itself: whether the feature is
 * locked, what it is, what it does, and which plan opens it.
 */
export type FeatureLock = {
  /** The plan does not grant this capability — render the locked state. */
  isLocked: boolean;
  /**
   * The answer isn't known yet. Callers must render NEITHER the unlocked UI nor
   * the locked state while this is true: optimistically showing a gated control
   * and then yanking it is worse than a beat of nothing.
   */
  isResolving: boolean;
  /**
   * The caller's ROLE can't use this control (only ever true when `access` was
   * passed). Role wins over plan: a role-blocked control is hidden, so
   * `isLocked` is forced false — never a plan lock, never an Upgrade CTA.
   */
  isRoleBlocked: boolean;
  /**
   * `access` was passed and the profile hasn't landed. Deliberately NOT part of
   * `isResolving`: `replace` mode waits on it (renders nothing), but `notice`/
   * `blur` keep rendering their children and the plan-lock affordance, exactly as
   * without `access`, until a resolved role blocks it. The profile is primed by
   * every route loader, so this window is normally empty.
   */
  isRoleResolving: boolean;
  /** What the feature is called in the UI. */
  label: string;
  /** One or two sentences on what the feature does. */
  preview: string;
  /** Cheapest purchasable plan that grants it, or `null` when none does. */
  unlockingPlan: BillingPlan | null;
  /** `unlockingPlan`'s tier name ("Growth"), or `null` — never an empty string. */
  unlockingPlanName: string | null;
};

type Options = {
  /**
   * Externally-resolved lock, bypassing `useCapabilities()` entirely.
   *
   * The warmup-settings sections do not resolve their gates through
   * `GET /billing/capabilities`: `GET /email-accounts/:id/warmup-settings`
   * already returns the resolved booleans (`customEspAllowed` /
   * `customContentAllowed` / `templatesAllowed` / `customScheduleAllowed` /
   * `customLanguageAllowed` / `replyRateAllowed`) alongside the settings they
   * gate, so the lock is
   * free there and can't disagree with the values it's guarding. Two distinct
   * caller families is what justifies this prop — resist adding a third knob.
   */
  locked?: boolean;
  /**
   * The (Feature, Op) the API checks for this control. The role check runs
   * FIRST: a role-blocked control reports `isRoleBlocked` and never locks. While
   * the role is unresolved `isResolving` is true. Omit for no role requirement.
   */
  access?: Access;
};

/**
 * Resolve a capability's lock state plus the copy and plan the locked UI needs.
 *
 * The plans catalog is fetched ONLY once we know the feature is locked. A gated
 * control sitting on a non-billing page (the bulk action bar, the API-keys
 * header) must not trigger an app-wide `GET /billing/plans` merely by existing —
 * for an entitled org the answer is "unlocked" and no plan resolution is needed
 * at all.
 */
export function useFeatureLock(
  identifier: CapabilityIdentifier,
  options?: Options,
): FeatureLock {
  const externalLocked = options?.locked;
  const isExternal = externalLocked !== undefined;

  const role = useRoleAccess(options?.access);
  const isRoleBlocked = role.isResolved && !role.allowed;

  // A role-blocked caller never needs the plan answer, so don't fetch it.
  const capabilities = useCapabilities({ enabled: !isExternal && !isRoleBlocked });

  // An errored capabilities read stays "resolving" forever rather than falling
  // through to either answer: showing the control would contradict the API's 403,
  // and showing an upsell to an org that may well be entitled is its own lie.
  const planResolving = isExternal ? false : capabilities.data === undefined;
  const isResolving = !isRoleBlocked && planResolving;
  const planLocked = isExternal
    ? externalLocked
    : capabilities.data !== undefined && !capabilities.data.includes(identifier);
  const isLocked = !isRoleBlocked && planLocked;

  const plansQuery = usePlansQuery({ enabled: isLocked });
  const unlockingPlan = isLocked
    ? resolveUnlockingPlan(plansQuery.data ?? [], identifier)
    : null;

  const copy = CAPABILITY_COPY[identifier];
  return {
    isLocked,
    isResolving,
    isRoleBlocked,
    isRoleResolving: !role.isResolved,
    label: copy.label,
    preview: copy.preview,
    unlockingPlan,
    unlockingPlanName: unlockingPlan ? planTierName(unlockingPlan) : null,
  };
}
